import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'
import { useAgentConfig } from '../../context/agent-config-context'
import { agentConfigSchema, type AgentConfig } from '../../schema'
import { StagesBuilder } from './stages-builder'

export function PersonalityTab() {
  const { config, updateConfig } = useAgentConfig()

  const form = useForm<AgentConfig>({
    resolver: zodResolver(agentConfigSchema),
    defaultValues: config,
    mode: 'onChange',
  })

  // Auto-save with debounce
  useEffect(() => {
    const subscription = form.watch((value) => {
      // Use a timeout to debounce updates
      const timeoutId = setTimeout(() => {
        updateConfig(value as Partial<AgentConfig>)
      }, 500) // 500ms debounce

      return () => clearTimeout(timeoutId)
    })
    return () => subscription.unsubscribe()
  }, [form.watch, updateConfig])

  return (
    <ScrollArea className='h-[calc(100vh-16rem)] pr-4'>
      <Form {...form}>
        <form className='space-y-6'>
          {/* Comportamento do Agente */}
          <div className='space-y-4'>
            <div>
              <h3 className='text-lg font-medium'>Comportamento do Agente</h3>
              <p className='text-sm text-muted-foreground'>
                Defina as instruções principais que guiarão o comportamento do
                agente.
              </p>
            </div>
            <FormField
              control={form.control}
              name='systemPrompt'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Prompt do Sistema</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder='Ex: Você é um assistente virtual especializado em atendimento ao cliente. Seja sempre cordial, objetivo e prestativo...'
                      rows={8}
                      className='resize-none'
                    />
                  </FormControl>
                  <FormDescription>
                    Esta é a descrição de cargo do seu agente. Seja específico
                    sobre o papel, regras e limitações.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <Separator />

          {/* Estágios/Condições */}
          <div className='space-y-4'>
            <div>
              <h3 className='text-lg font-medium'>
                Estágios e Condições (Fluxo Conversacional)
              </h3>
              <p className='text-sm text-muted-foreground'>
                Configure diferentes estágios na conversa com instruções e
                gatilhos específicos.
              </p>
            </div>
            <FormField
              control={form.control}
              name='stages'
              render={({ field }) => (
                <FormItem>
                  <StagesBuilder
                    stages={field.value || []}
                    onChange={field.onChange}
                  />
                </FormItem>
              )}
            />
          </div>

          <Separator />

          {/* Configurações Básicas */}
          <div className='space-y-4'>
            <div>
              <h3 className='text-lg font-medium'>Configurações</h3>
              <p className='text-sm text-muted-foreground'>
                Defina as configurações básicas do agente.
              </p>
            </div>

            <div className='grid gap-4 md:grid-cols-2'>
              <FormField
                control={form.control}
                name='name'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome do Agente</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder='Ex: Agente de Suporte' />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='tone'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tom de Voz</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder='Selecione o tom' />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value='practical'>Prático</SelectItem>
                        <SelectItem value='formal'>Formal</SelectItem>
                        <SelectItem value='casual'>Casual</SelectItem>
                        <SelectItem value='funny'>Engraçado</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='model'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Modelo do Agente</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder='Selecione o modelo' />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value='gpt-4o-mini'>
                          GPT-4o Mini (1 crédito/msg)
                        </SelectItem>
                        <SelectItem value='gpt-4o'>GPT-4o</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='language'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Idioma</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder='Selecione o idioma' />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value='auto'>
                          Detecção Automática
                        </SelectItem>
                        <SelectItem value='pt-BR'>Português (BR)</SelectItem>
                        <SelectItem value='en-US'>Inglês (US)</SelectItem>
                        <SelectItem value='es-ES'>Espanhol</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='timezone'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Fuso Horário</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder='Selecione o fuso horário' />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value='America/Sao_Paulo'>
                          São Paulo (GMT-3)
                        </SelectItem>
                        <SelectItem value='America/New_York'>
                          Nova York (GMT-5)
                        </SelectItem>
                        <SelectItem value='Europe/London'>
                          Londres (GMT+0)
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='responseDelay'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Delay para Resposta (segundos)</FormLabel>
                    <FormControl>
                      <Input
                        type='number'
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                        min={0}
                        max={300}
                      />
                    </FormControl>
                    <FormDescription>
                      Tempo de espera antes de responder, útil para concatenar
                      múltiplas mensagens.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name='initialMessage'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Mensagem Inicial</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder='Ex: Olá! Como posso ajudar você hoje?'
                      rows={3}
                    />
                  </FormControl>
                  <FormDescription>
                    Primeira mensagem enviada quando o usuário inicia a
                    conversa.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <Separator />

          {/* Perfil da Empresa */}
          <Accordion type='single' collapsible className='w-full'>
            <AccordionItem value='company'>
              <AccordionTrigger>
                <div>
                  <h3 className='text-lg font-medium'>Perfil da Empresa</h3>
                  <p className='text-sm text-muted-foreground'>
                    Informações contextuais sobre a empresa (opcional).
                  </p>
                </div>
              </AccordionTrigger>
              <AccordionContent className='pt-4 space-y-4'>
                <div className='grid gap-4 md:grid-cols-2'>
                  <FormField
                    control={form.control}
                    name='companyName'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nome da Empresa</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder='Ex: WazAI' />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name='companySector'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Setor</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder='Ex: Tecnologia e Automação'
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name='companyWebsite'
                    render={({ field }) => (
                      <FormItem className='md:col-span-2'>
                        <FormLabel>Website</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type='url'
                            placeholder='https://exemplo.com.br'
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name='companyDescription'
                    render={({ field }) => (
                      <FormItem className='md:col-span-2'>
                        <FormLabel>Sobre a Empresa</FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            placeholder='Descreva sua empresa, produtos, missão e valores...'
                            rows={4}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </form>
      </Form>
    </ScrollArea>
  )
}
